import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, Card, Loading, Button, Modal, Field, Pill, EmptyState } from "@/components/kit";
import { UserPlus, Trash2, ShieldCheck, Users } from "lucide-react";

const LINES = ["Padel", "Badminton", "Minisoccer", "Hyrox", "Gym Studio", "Cafe"];

export default function Team() {
  const [admins, setAdmins] = useState(null);
  const [coaches, setCoaches] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showCoach, setShowCoach] = useState(false);

  const load = () => {
    api.ownerAdmins().then(setAdmins).catch(() => setAdmins([]));
    api.listCoaches().then(setCoaches).catch(() => setCoaches([]));
  };
  useEffect(() => { load(); }, []);

  const delCoach = async (id) => {
    if (!window.confirm("Hapus coach ini?")) return;
    try { await api.deleteCoach(id); toast.success("Terhapus"); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <PageHeader eyebrow="Owner" title="Tim & Coach" subtitle="Kelola admin lini bisnis dan pelatih (coach) klub." />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <div className="hsc-label"><ShieldCheck className="mr-2 inline h-4 w-4" /> Admin</div>
            <Button variant="outline" onClick={() => setShowAdmin(true)} data-testid="add-admin"><UserPlus className="h-4 w-4" /> Admin</Button>
          </div>
          {admins === null ? <Loading /> : !admins.length ? <EmptyState title="Belum ada admin" /> : (
            <div className="space-y-2">
              {admins.map((a) => (
                <div key={a.id} className="rounded-lg border border-[#1F1F22] px-3 py-2.5">
                  <div className="text-sm text-white">{a.name}</div>
                  <div className="text-[11px] text-[#6E6E73]">{a.email}</div>
                  <div className="mt-1 text-[11px] text-[#8FD3E8]">{a.all_access ? "ALL ACCESS" : (a.lines || []).join(", ") || a.line}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="mb-4 flex items-center justify-between">
            <div className="hsc-label"><Users className="mr-2 inline h-4 w-4" /> Coach</div>
            <Button variant="outline" onClick={() => setShowCoach(true)} data-testid="add-coach"><UserPlus className="h-4 w-4" /> Coach</Button>
          </div>
          {coaches === null ? <Loading /> : !coaches.length ? <EmptyState title="Belum ada coach" /> : (
            <div className="space-y-2">
              {coaches.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-lg border border-[#1F1F22] px-3 py-2.5">
                  <div><div className="text-sm text-white">{c.name}</div><div className="text-[11px] text-[#6E6E73]">{c.email}</div></div>
                  <button className="rounded-lg p-2 text-[#8E8E93] hover:bg-[#1a0d0d] hover:text-[#FF6B6B]" onClick={() => delCoach(c.id)}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {showAdmin && <AdminModal onClose={() => setShowAdmin(false)} onSaved={() => { setShowAdmin(false); load(); }} />}
      {showCoach && <CoachModal onClose={() => setShowCoach(false)} onSaved={() => { setShowCoach(false); load(); }} />}
    </div>
  );
}

function AdminModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [lines, setLines] = useState([]);
  const [allAccess, setAllAccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggleLine = (l) => setLines((s) => s.includes(l) ? s.filter((x) => x !== l) : [...s, l]);
  const save = async () => {
    if (!f.email || f.password.length < 6) return toast.error("Email & password (min 6) wajib");
    if (!allAccess && !lines.length) return toast.error("Pilih minimal satu lini atau All Access");
    setBusy(true);
    try { await api.ownerCreateAdmin(f.email.trim(), f.password, f.name.trim(), { lines, all_access: allAccess }); toast.success("Admin dibuat"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open title="Admin Baru" onClose={onClose}>
      <div className="space-y-4">
        <Field label="Nama"><input className="hsc-input" value={f.name} onChange={set("name")} /></Field>
        <Field label="Email"><input className="hsc-input" value={f.email} onChange={set("email")} /></Field>
        <Field label="Password"><input type="password" className="hsc-input" value={f.password} onChange={set("password")} /></Field>
        <Field label="Akses Lini">
          <div className="flex flex-wrap gap-2">
            <Pill active={allAccess} onClick={() => setAllAccess(!allAccess)}>All Access</Pill>
            {!allAccess && LINES.map((l) => <Pill key={l} active={lines.includes(l)} onClick={() => toggleLine(l)}>{l}</Pill>)}
          </div>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={busy}>Simpan</Button>
        </div>
      </div>
    </Modal>
  );
}

function CoachModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    if (!f.name || !f.email || f.password.length < 6) return toast.error("Semua field wajib (password min 6)");
    setBusy(true);
    try { await api.createCoach({ name: f.name.trim(), email: f.email.trim(), password: f.password }); toast.success("Coach dibuat"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open title="Coach Baru" onClose={onClose}>
      <div className="space-y-4">
        <Field label="Nama"><input className="hsc-input" value={f.name} onChange={set("name")} /></Field>
        <Field label="Email"><input className="hsc-input" value={f.email} onChange={set("email")} /></Field>
        <Field label="Password"><input type="password" className="hsc-input" value={f.password} onChange={set("password")} /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={busy}>Simpan</Button>
        </div>
      </div>
    </Modal>
  );
}
